import api from './apiService';

export interface PaymentMethodCategory {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  icon: string | null;
  color_code: string | null;
  sort_order: number;
}

export interface PaymentMethod {
  id: number;
  code: string;
  name: string;
  provider: string | null;
  logo: string | null;
  description: string | null;
  instructions: string | null;
  customer_fee_type: 'none' | 'fixed' | 'percentage';
  customer_fee_amount: number;
  customer_fee_percentage: number;
  fee_calculation: string;
  min_amount: number;
  max_amount: number;
  processing_time: string | null;
  requires_authentication: boolean;
  category: PaymentMethodCategory;
  sort_order: number;
}

export interface PaymentMethodApiResponse {
  success: boolean;
  message: string;
  data: PaymentMethod[];
}

export interface DetailTimeSlots {
  public_id: string;
  day_of_week: string;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  final_price: number;
  is_available: boolean;
  dp_price: number;
}

export interface DetailTimeSlotsResponse {
  success: boolean;
  message: string;
  data: DetailTimeSlots[];
}

/**
 * Payment method service for managing payment method-related API calls
 */
export class PaymentMethodApiService {
  /**
   * Get all available payment methods
   */
  static async getPaymentMethods(): Promise<PaymentMethod[]> {
    try {
      const response = await api.get<PaymentMethodApiResponse>('payment-methods');

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch payment methods');
      }

      // Sort by sort_order and return
      const paymentMethods = response.data.data.sort((a, b) => a.sort_order - b.sort_order);
      return Array.isArray(paymentMethods) ? paymentMethods : [];
    } catch (error: any) {
      console.error('Error fetching payment methods:', error);
      // Return empty array on error to prevent map() issues
      return [];
    }
  }

  /**
   * Calculate customer fee for a payment method
   * @param paymentMethod - The payment method object
   * @param amount - The base amount to calculate fee from
   * @returns The calculated fee amount
   */
  static calculateCustomerFee(paymentMethod: PaymentMethod, amount: number): number {
    if (!paymentMethod || paymentMethod.customer_fee_type === 'none') {
      return 0;
    }

    switch (paymentMethod.customer_fee_type) {
      case 'fixed':
        return paymentMethod.customer_fee_amount;

      case 'percentage':
        return Math.round((amount * paymentMethod.customer_fee_percentage) / 100);

      default:
        return 0;
    }
  }

  /**
   * Get payment method icon based on code
   * @param code - Payment method code
   * @returns Icon string for iconify
   */
  static getPaymentMethodIcon(code: string): string {
    const iconMap: Record<string, string> = {
      'virtual-account': 'mdi:bank-outline',
      'qr-code': 'mdi:qrcode',
      'e-wallet': 'mdi:wallet-outline',
      cash: 'mdi:cash',
      retail: 'mdi:store-outline',
    };

    return iconMap[code] || 'mdi:credit-card-outline';
  }

  static async getDetailTimeslots(
    publicIDs: string[],
    arenaSlug: string
  ): Promise<DetailTimeSlotsResponse> {
    try {
      const response = await api.post(`/arena/${arenaSlug}/detail-time-slots`, {
        public_slot_id: publicIDs,
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching timeslot details:', error);
      throw error;
    }
  }
}

export default PaymentMethodApiService;
